import { useMemo, useState } from 'react'
import { PiArrowUpRight } from 'react-icons/pi'

import { FbModal } from 'components/UI/Modal'
import { Select } from 'components/UI/Select/Select'
import { useGetSendableEmailIntegrationOptions } from 'services/emailIntegrationApi'

import type { Reply } from 'features/campaigns/replies_api'

interface TakeOverModalProps {
  reply: Reply
  show: boolean
  onClose: () => void
  /**
   * Called when the operator clicks the click-through CTA. Receives the
   * integration id they want to take the conversation over from. The parent
   * is then responsible for opening the inline reply composer; the modal
   * never fires the take-over mutation itself.
   */
  onConfirm: (integrationId: number) => void
}

/**
 * Informational modal explaining what "Take over" does.
 *
 * The take-over does NOT happen here. It commits when the operator submits
 * their first reply in the inline composer that opens after this modal closes.
 * That keeps the modal a one-time explainer rather than a confirmation step.
 */
export function TakeOverModal({
  reply,
  show,
  onClose,
  onConfirm,
}: TakeOverModalProps) {
  const { data: integrationOptions } = useGetSendableEmailIntegrationOptions()
  const workEmailOptions = useMemo(
    () => integrationOptions?.filter((opt) => opt.value > 0) ?? [],
    [integrationOptions]
  )

  // Track the user's explicit pick separately from the default. Deriving
  // the effective id this way avoids a render-time `setState` (or a banned
  // `useEffect`) when `workEmailOptions` resolves after the first render.
  const [userSelectedIntegrationId, setUserSelectedIntegrationId] = useState<
    number | null
  >(null)
  const selectedIntegrationId =
    userSelectedIntegrationId ?? workEmailOptions[0]?.value ?? null

  const selectItems = useMemo(
    () =>
      workEmailOptions.map((opt) => ({ label: opt.label, value: opt.value })),
    [workEmailOptions]
  )

  return (
    <FbModal
      open={show}
      onOpenChange={(o: boolean) => {
        if (!o) onClose()
      }}
      title={
        <span className="flex items-center gap-2">
          <PiArrowUpRight className="size-5 text-brand" />
          Take over this conversation
        </span>
      }
      description="Continue from your work email"
      acceptButtonText="Got it, write reply"
      cancelButtonText="Cancel"
      onAccept={() => {
        if (selectedIntegrationId == null) return
        onConfirm(selectedIntegrationId)
      }}
      onCancel={onClose}
      blockAccept={selectedIntegrationId == null}
    >
      <div className="flex flex-col gap-5 px-1">
        <ul className="flex flex-col gap-2 text-sm text-fg-secondary">
          <Bullet>
            From here on, this conversation moves to your chosen work email.
            Future replies land in your inbox, not on the campaign sender.
          </Bullet>
          <Bullet>
            From the prospect&apos;s side it threads as a normal reply. Same
            conversation, just from your address, with no{' '}
            <span className="font-mono text-xs">Fwd:</span> or campaign
            branding.
          </Bullet>
          <Bullet>You can only take over a reply once.</Bullet>
        </ul>

        <div className="flex items-center gap-2 text-sm text-fg-secondary">
          <span className="text-fg-muted">From your inbox:</span>
          <Select<number>
            items={selectItems}
            selected={selectedIntegrationId ?? undefined}
            onSelect={(value) => {
              if (typeof value === 'number') setUserSelectedIntegrationId(value)
            }}
            buttonVariant="secondary"
            buttonSize="sm"
            matchTriggerWidth
            buttonClassName="min-w-[260px] justify-between"
          />
        </div>

        {/* Hidden context for screen readers, also nice for debugging */}
        <span className="sr-only">
          Taking over the campaign reply from {reply.from_email_address}.
        </span>
      </div>
    </FbModal>
  )
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span
        aria-hidden
        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand"
      />
      <span>{children}</span>
    </li>
  )
}
